import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { organizationsAPI, platformAPI } from '../lib/api';
import { formatDate, cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
    Building2,
    Plus,
    Users,
    Package,
    Crown,
    Search,
    RefreshCw,
    Edit,
    UserPlus,
    CreditCard,
    CheckCircle,
    Clock,
    XCircle,
    TrendingUp
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const PLAN_LABELS = {
    starter: { label: 'Starter', color: 'bg-gray-100 text-gray-700', icon: Package },
    pro: { label: 'Pro', color: 'bg-blue-100 text-blue-700', icon: TrendingUp },
    enterprise: { label: 'Enterprise', color: 'bg-purple-100 text-purple-700', icon: Crown },
};

const STATUS_LABELS = {
    trial: { label: 'Essai', color: 'bg-amber-100 text-amber-700', icon: Clock },
    active: { label: 'Actif', color: 'bg-green-100 text-green-700', icon: CheckCircle },
    expired: { label: 'Expiré', color: 'bg-red-100 text-red-700', icon: XCircle },
    cancelled: { label: 'Annulé', color: 'bg-gray-100 text-gray-700', icon: XCircle },
};

export default function Organizations() {
    const { user } = useAuth();
    const [organizations, setOrganizations] = useState([]);
    const [platformStats, setPlatformStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    
    // Dialogs
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [adminDialogOpen, setAdminDialogOpen] = useState(false);
    const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
    const [selectedOrg, setSelectedOrg] = useState(null);
    
    // Form states
    const [newOrg, setNewOrg] = useState({ name: '', slug: '', email: '', phone: '', wilaya: '' });
    const [newAdmin, setNewAdmin] = useState({ name: '', email: '', password: '' });
    const [newSubscription, setNewSubscription] = useState({ plan: 'pro', duration_months: 1 });
    const [submitting, setSubmitting] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            const [orgsRes, statsRes] = await Promise.all([
                organizationsAPI.getAll(),
                platformAPI.getStats()
            ]);
            setOrganizations(orgsRes.data);
            setPlatformStats(statsRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (user?.role === 'super_admin') {
            fetchData();
        }
    }, [fetchData, user]);

    // Filter organizations
    const filteredOrgs = organizations.filter(org =>
        org.name.toLowerCase().includes(search.toLowerCase()) ||
        org.slug.toLowerCase().includes(search.toLowerCase())
    );

    // Create organization
    const handleCreateOrg = async () => {
        if (!newOrg.name || !newOrg.slug) {
            toast.error('Nom et slug sont requis');
            return;
        }
        
        setSubmitting(true);
        try {
            await organizationsAPI.create(newOrg);
            toast.success('Organisation créée!');
            setCreateDialogOpen(false);
            setNewOrg({ name: '', slug: '', email: '', phone: '', wilaya: '' });
            fetchData();
        } catch (error) {
            const message = error.response?.data?.detail || 'Erreur lors de la création';
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    // Update organization
    const handleUpdateOrg = async () => {
        if (!selectedOrg) return;
        
        setSubmitting(true);
        try {
            await organizationsAPI.update(selectedOrg.id, selectedOrg);
            toast.success('Organisation mise à jour!');
            setEditDialogOpen(false);
            fetchData();
        } catch (error) {
            const message = error.response?.data?.detail || 'Erreur lors de la mise à jour';
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    // Create admin for org
    const handleCreateAdmin = async () => {
        if (!selectedOrg || !newAdmin.name || !newAdmin.email || !newAdmin.password) {
            toast.error('Tous les champs sont requis');
            return;
        }
        
        setSubmitting(true);
        try {
            await organizationsAPI.createAdmin(selectedOrg.id, newAdmin);
            toast.success(`Admin créé pour ${selectedOrg.name}!`);
            setAdminDialogOpen(false);
            setNewAdmin({ name: '', email: '', password: '' });
            fetchData();
        } catch (error) {
            const message = error.response?.data?.detail || 'Erreur lors de la création';
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    // Update subscription
    const handleUpdateSubscription = async () => {
        if (!selectedOrg) return;
        
        setSubmitting(true);
        try {
            await organizationsAPI.updateSubscription(selectedOrg.id, newSubscription);
            toast.success('Abonnement mis à jour!');
            setSubscriptionDialogOpen(false);
            fetchData();
        } catch (error) {
            const message = error.response?.data?.detail || 'Erreur lors de la mise à jour';
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    // Auto-generate slug from name
    const handleNameChange = (name) => {
        setNewOrg({
            ...newOrg,
            name,
            slug: name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
        });
    };

    if (user?.role !== 'super_admin') {
        return (
            <Layout>
                <div className="flex items-center justify-center h-96">
                    <p className="text-muted-foreground">Accès réservé au Super Administrateur</p>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-3">
                            <Building2 className="w-8 h-8 text-primary" />
                            Gestion des Organisations
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Gérez vos clients et leurs abonnements
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={fetchData}
                            disabled={loading}
                        >
                            <RefreshCw className={cn('w-4 h-4 mr-2', loading && 'animate-spin')} />
                            Actualiser
                        </Button>
                        <Button className="btn-luxury" onClick={() => setCreateDialogOpen(true)}>
                            <Plus className="w-4 h-4 mr-2" />
                            Nouvelle Organisation
                        </Button>
                    </div>
                </div>

                {/* Platform Stats */}
                {platformStats && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <Card className="bg-blue-50 border-blue-200">
                            <CardContent className="p-4 flex items-center gap-4">
                                <div className="p-3 bg-blue-100 rounded-lg">
                                    <Building2 className="w-6 h-6 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-blue-600">Organisations</p>
                                    <p className="text-2xl font-bold text-blue-700">{platformStats.total_organizations}</p>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="bg-green-50 border-green-200">
                            <CardContent className="p-4 flex items-center gap-4">
                                <div className="p-3 bg-green-100 rounded-lg">
                                    <CheckCircle className="w-6 h-6 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-green-600">Actives</p>
                                    <p className="text-2xl font-bold text-green-700">{platformStats.active_organizations}</p>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="bg-purple-50 border-purple-200">
                            <CardContent className="p-4 flex items-center gap-4">
                                <div className="p-3 bg-purple-100 rounded-lg">
                                    <Package className="w-6 h-6 text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-purple-600">Total Commandes</p>
                                    <p className="text-2xl font-bold text-purple-700">{platformStats.total_orders}</p>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="bg-amber-50 border-amber-200">
                            <CardContent className="p-4 flex items-center gap-4">
                                <div className="p-3 bg-amber-100 rounded-lg">
                                    <Users className="w-6 h-6 text-amber-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-amber-600">Total Utilisateurs</p>
                                    <p className="text-2xl font-bold text-amber-700">{platformStats.total_users}</p>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Search */}
                <Card className="card-luxury">
                    <CardContent className="p-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Rechercher une organisation..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Organizations Table */}
                <Card className="card-luxury">
                    <CardHeader>
                        <CardTitle>Organisations ({filteredOrgs.length})</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        {loading ? (
                            <div className="flex justify-center py-12">
                                <div className="spinner" />
                            </div>
                        ) : filteredOrgs.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">
                                <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p>Aucune organisation trouvée</p>
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Organisation</TableHead>
                                        <TableHead>Slug</TableHead>
                                        <TableHead>Plan</TableHead>
                                        <TableHead>Statut</TableHead>
                                        <TableHead>Commandes</TableHead>
                                        <TableHead>Utilisateurs</TableHead>
                                        <TableHead>Créé le</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredOrgs.map((org) => {
                                        const plan = PLAN_LABELS[org.subscription_plan] || PLAN_LABELS.starter;
                                        const status = STATUS_LABELS[org.subscription_status] || STATUS_LABELS.trial;
                                        const PlanIcon = plan.icon;
                                        const StatusIcon = status.icon;
                                        
                                        return (
                                            <TableRow key={org.id}>
                                                <TableCell>
                                                    <div className="font-medium">{org.name}</div>
                                                    {org.email && (
                                                        <div className="text-sm text-muted-foreground">{org.email}</div>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <code className="text-sm bg-muted px-2 py-1 rounded">
                                                        /{org.slug}
                                                    </code>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge className={cn(plan.color, 'flex items-center gap-1 w-fit')}>
                                                        <PlanIcon className="w-3 h-3" />
                                                        {plan.label}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge className={cn(status.color, 'flex items-center gap-1 w-fit')}>
                                                        <StatusIcon className="w-3 h-3" />
                                                        {status.label}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>{org.order_count}</TableCell>
                                                <TableCell>{org.user_count}</TableCell>
                                                <TableCell>{formatDate(org.created_at)}</TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex justify-end gap-1">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => {
                                                                setSelectedOrg(org);
                                                                setEditDialogOpen(true);
                                                            }}
                                                        >
                                                            <Edit className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => {
                                                                setSelectedOrg(org);
                                                                setAdminDialogOpen(true);
                                                            }}
                                                        >
                                                            <UserPlus className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => {
                                                                setSelectedOrg(org);
                                                                setNewSubscription({ plan: org.subscription_plan, duration_months: 1 });
                                                                setSubscriptionDialogOpen(true);
                                                            }}
                                                        >
                                                            <CreditCard className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>

                {/* Create Organization Dialog */}
                <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Building2 className="w-5 h-5" />
                                Nouvelle Organisation
                            </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label>Nom de l'entreprise *</Label>
                                <Input
                                    placeholder="Ex: Parfum Oran"
                                    value={newOrg.name}
                                    onChange={(e) => handleNameChange(e.target.value)}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Slug (URL) *</Label>
                                <div className="flex items-center gap-2">
                                    <span className="text-muted-foreground">parfum-app.dz/</span>
                                    <Input
                                        placeholder="parfum-oran"
                                        value={newOrg.slug}
                                        onChange={(e) => setNewOrg({ ...newOrg, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Email</Label>
                                    <Input
                                        type="email"
                                        placeholder="contact@parfum-oran.dz"
                                        value={newOrg.email}
                                        onChange={(e) => setNewOrg({ ...newOrg, email: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Téléphone</Label>
                                    <Input
                                        placeholder="0555 XX XX XX"
                                        value={newOrg.phone}
                                        onChange={(e) => setNewOrg({ ...newOrg, phone: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Wilaya</Label>
                                <Input
                                    placeholder="Oran"
                                    value={newOrg.wilaya}
                                    onChange={(e) => setNewOrg({ ...newOrg, wilaya: e.target.value })}
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                                Annuler
                            </Button>
                            <Button className="btn-luxury" onClick={handleCreateOrg} disabled={submitting}>
                                {submitting ? 'Création...' : 'Créer'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Edit Organization Dialog */}
                <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Edit className="w-5 h-5" />
                                Modifier {selectedOrg?.name}
                            </DialogTitle>
                        </DialogHeader>
                        {selectedOrg && (
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label>Nom</Label>
                                    <Input
                                        value={selectedOrg.name}
                                        onChange={(e) => setSelectedOrg({ ...selectedOrg, name: e.target.value })}
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Email</Label>
                                        <Input
                                            type="email"
                                            value={selectedOrg.email || ''}
                                            onChange={(e) => setSelectedOrg({ ...selectedOrg, email: e.target.value })}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Téléphone</Label>
                                        <Input
                                            value={selectedOrg.phone || ''}
                                            onChange={(e) => setSelectedOrg({ ...selectedOrg, phone: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Actif</Label>
                                    <Select
                                        value={selectedOrg.is_active ? 'true' : 'false'}
                                        onValueChange={(v) => setSelectedOrg({ ...selectedOrg, is_active: v === 'true' })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="true">Actif</SelectItem>
                                            <SelectItem value="false">Désactivé</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        )}
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                                Annuler
                            </Button>
                            <Button className="btn-luxury" onClick={handleUpdateOrg} disabled={submitting}>
                                {submitting ? 'Enregistrement...' : 'Enregistrer'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Create Admin Dialog */}
                <Dialog open={adminDialogOpen} onOpenChange={setAdminDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <UserPlus className="w-5 h-5" />
                                Créer Admin pour {selectedOrg?.name}
                            </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label>Nom complet *</Label>
                                <Input
                                    placeholder="Nom de l'administrateur"
                                    value={newAdmin.name}
                                    onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Email *</Label>
                                <Input
                                    type="email"
                                    placeholder="admin@example.com"
                                    value={newAdmin.email}
                                    onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Mot de passe *</Label>
                                <Input
                                    type="password"
                                    placeholder="••••••••"
                                    value={newAdmin.password}
                                    onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setAdminDialogOpen(false)}>
                                Annuler
                            </Button>
                            <Button className="btn-luxury" onClick={handleCreateAdmin} disabled={submitting}>
                                {submitting ? 'Création...' : 'Créer Admin'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Update Subscription Dialog */}
                <Dialog open={subscriptionDialogOpen} onOpenChange={setSubscriptionDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <CreditCard className="w-5 h-5" />
                                Abonnement - {selectedOrg?.name}
                            </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label>Plan</Label>
                                <Select
                                    value={newSubscription.plan}
                                    onValueChange={(v) => setNewSubscription({ ...newSubscription, plan: v })}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="starter">Starter (Gratuit)</SelectItem>
                                        <SelectItem value="pro">Pro (5,000 DA/mois)</SelectItem>
                                        <SelectItem value="enterprise">Enterprise (15,000 DA/mois)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Durée (mois)</Label>
                                <Select
                                    value={String(newSubscription.duration_months)}
                                    onValueChange={(v) => setNewSubscription({ ...newSubscription, duration_months: parseInt(v) })}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="1">1 mois</SelectItem>
                                        <SelectItem value="3">3 mois</SelectItem>
                                        <SelectItem value="6">6 mois</SelectItem>
                                        <SelectItem value="12">12 mois</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setSubscriptionDialogOpen(false)}>
                                Annuler
                            </Button>
                            <Button className="btn-luxury" onClick={handleUpdateSubscription} disabled={submitting}>
                                {submitting ? 'Mise à jour...' : 'Mettre à jour'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}
